import { Box, ButtonBase, IconButton, useMediaQuery, useTheme } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import { tokens } from "../../../../../theme";
import Text from "../../../../theme/text/Text";

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../../utils/permissions";

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useTranslation } from "react-i18next";
import { ReactComponent as Crate } from '../../../../../styles/svg/crate.svg';
import { useCreditOrderCostsMutation, useUpdateOrderLinesMutation, useGetOrderLinesQuery } from "../../../../../newapi/order/ordersSlice";
import { showToastMessagePostRequest, showToastMessageRequestError, showToastMessageUpdateRequest } from "../../../../../utils/toasts";
// import SPStockDetails from "../../SpStockDetails/SPStockDetails";
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import EditIcon from '@mui/icons-material/Edit';
import UndoIcon from '@mui/icons-material/Undo';
import { useGetLogsQuery } from "../../../../../newapi/global/logsSlice";
import { ReactComponent as BolLogo } from '../../../../../styles/svg/bol.svg';
import { ReactComponent as CirroLogo } from '../../../../../styles/svg/cirro.svg';
import { ReactComponent as DhlLogo } from '../../../../../styles/svg/dhl.svg';
import { ReactComponent as DpdLogo } from '../../../../../styles/svg/dpd.svg';
import { ReactComponent as HstLogo } from '../../../../../styles/svg/hst.svg';
import { ReactComponent as PostNLLogo } from '../../../../../styles/svg/postnl.svg';
import { formatPrice } from '../../../../../utils/helpers';
import { general_states } from "../../../../../utils/staticEnums";
import { getShipperColorSettingsByType } from "../../../../Streams/StreamShipperSettings";
import SHQuickAction from '../../../../theme/Modal/SHQuickAction';
import SidepanelLoader from "../../../../theme/sidepanel2/SidepanelLoader";
import Heading from "../../../../theme/text/Heading";
import Subheading from "../../../../theme/text/Subheading";
import MConfirmation from '../../../Modals/MConfirmation';
import MUpdateOrderInvoiceProducts from "../../../Modals/MUpdateOrderInvoiceProducts";
import MUpdateOrderShipper from "../../../Modals/MUpdateOrderShipper";
import { ENV_TYPES } from "../../../Sidebar";
import { OrderlinesComp } from "../components/OrderlinesComp";
import { OrderEvents } from "../components/OrderEvents";
import { getTimeLineSettings } from "../TimeLineMap";
import { booleanValueTypes } from "../../../../Streams/Conditions/ConditionSettings";
import { useGetProductsSelectQuery } from '../../../../../newapi/warehouse/productSlice';
import ShValidatedInputDropdown from '../../../../theme/dropdowns/ShValidatedInputDropdown';
import NumberInput from "../../../../theme/inputs/NumberInput";
import SpDesktopFooter from '../../../../theme/sidepanel2/SidepanelFooter';
import { toast } from 'react-toastify';
import ButtonGroup from "../../../../theme/buttons/ButtonGroup";
import Shbutton from "../../../../theme/buttons/Shbutton";

const SpStockDetails = SidepanelLoader(() => import("../../SpStockDetails/SPStockDetails"));
const SpVirtualProductDetails = SidepanelLoader(() => import("../../SpVirtualProductDetails/SpVirtualProductDetails"));
const SpReturnDetails = SidepanelLoader(() => import("../../SpReturnDetails/SpReturnDetails"));

const EditViewActions = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    return (
        <ButtonGroup version={2}>
            <Shbutton
                className={"group-btn"}
                color="grey"
                variant="outlined"
                onClick={props.onCancel}
                disabled={props.isLoading}
            >
                {t("cancel")}
            </Shbutton>
            <Shbutton
                className={"group-btn"}
                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                variant="contained"
                onClick={props.onSave}
                disabled={props.isLoading}
                isLoading={props.isLoading}
            >
                {t("save")}
            </Shbutton>
        </ButtonGroup>
    )
}

const OrderLinesEditView = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);

    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = selectedEnvironmentType === ENV_TYPES[200];
    const selectedId = useSelector((state) => state.settings.environment.selectedId);


    const { data: products } = useGetProductsSelectQuery({relationId: isRelation ? selectedId : props.data?.relationId, addStock: true},
        { skip: !isRelation && !props.data?.relationId }
    );

    // Fetch existing order lines
    const { data: existingLinesData, isLoading: isLoadingLines } = useGetOrderLinesQuery({
        args: {
            id: props.data?.id,
            page: 1,
            size: 1000,
            body: {}
        }
    }, {
        skip: !props.data?.id
    });

    const [updateOrderLines, { isLoading: isSaving }] = useUpdateOrderLinesMutation();

    const [productOptions, setProductOptions] = useState([]);
    const [newOrderLines, setNewOrderLines] = useState([{
        sku: "",
        amount: 0,
        index: 1
    }]);

    useEffect(() => {
        if (products) {
            const mappedProducts = products.map(product => ({
                id: product.sku,
                name: product.combined,
                barcodes:product.barcodes,
            }));
            setProductOptions(mappedProducts);
        }
    }, [products]);

    // Initialize with existing order lines from API
    useEffect(() => {
        if (existingLinesData?.data && existingLinesData.data.length > 0) {
            // Filter to only include top-level orderlines (parentProductId: null) and exclude add-ons
            // These are the only ones that can be edited
            const topLevelLines = existingLinesData.data.filter(line =>
                (line.parentProductId === null || line.parentProductId === undefined) &&
                line.type !== 'add_on'
            );

            const linesCopy = topLevelLines.map((line, index) => ({
                sku: line.product?.sku || line.sku || "",
                amount: line.amount || 0,
                index: index + 1
            }));

            linesCopy.push({
                sku: "",
                amount: 0,
                index: linesCopy[linesCopy.length - 1].index + 1
            });

            setNewOrderLines(linesCopy);
        } else if (!isLoadingLines && props.data?.id) {
            // If there are no existing lines, start with one empty line
            setNewOrderLines([{
                sku: "",
                amount: 0,
                index: 1
            }]);
        }
    }, [existingLinesData, isLoadingLines, props.data?.id]);

    const handleChange = (index, key, value) => {
        const orderLinesCopy = [...newOrderLines];
        const lineToUpdate = orderLinesCopy.find(line => line.index === index);

        if (lineToUpdate) {
            lineToUpdate[key] = value;

            if (orderLinesCopy[orderLinesCopy.length - 1].sku.length > 0) {
                orderLinesCopy.push({
                    sku: "",
                    amount: 0,
                    index: orderLinesCopy[orderLinesCopy.length - 1].index + 1
                });
            }

            setNewOrderLines(orderLinesCopy);
        }
    };

    const handleProductSelect = (key, val) => {
        const lineIndex = parseInt(key.split(".")[0]);
        const lineKey = key.split(".")[1];

        handleChange(lineIndex, lineKey, val);
    }

    const handleDeleteLineRow = (index) => {
        const updatedOrderLines = newOrderLines.filter(line => line.index !== index);
        setNewOrderLines(updatedOrderLines);
    }

    // A line can only be removed when it is a real line (not the empty add-row) and at
    // least one other real line remains, so an order can never end up without lines.
    const realLineCount = newOrderLines.filter(line => line.sku.length > 0).length;
    const canDeleteLine = (orderLine) => orderLine.sku.length > 0 && realLineCount > 1;

    const handleCancel = useCallback(() => {
        // Switch back to view mode without saving
        if (props.setConfig) {
            props.setConfig(config => ({
                ...config,
                is_editting: null,
            }));
        }
    }, [props]);

    const handleSave = useCallback(async () => {
        const mappedOrderLines = newOrderLines
            .filter(x => x.sku.length > 0 && x.amount > 0)
            .map(x => ({
                sku: x.sku,
                amount: x.amount,
            }));

        try {
            const response = await updateOrderLines({
                orderId: props.data.id,
                body: mappedOrderLines
            }).unwrap();

            showToastMessageUpdateRequest(t, response);

            // Switch back to view mode
            if (props.setConfig) {
                props.setConfig(config => ({
                    ...config,
                    is_editting: null,
                }));
            }
        } catch (error) {
            toast.error(t('failed_to_update_order_lines'), {
                position: toast.POSITION.TOP_RIGHT
            });
        }
    }, [newOrderLines, updateOrderLines, props, t]);

    // Show loading text in the main interface instead of blocking the entire view
    const isStillLoading = isLoadingLines && newOrderLines.length === 1 && !newOrderLines[0].sku;

    return (
        <Box paddingX={0} paddingY={3}>
            <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} mb={2} px={3}>
                <Text bold fontSize={18}>{t("edit_order_lines")}</Text>
                {isStillLoading && <Text light>{t("loading")}...</Text>}
            </Box>

            {["shopify", "bol"].includes(props.data?.webshopcredentials?.webshop_type) && (
                <Box
                    sx={{
                        background: colors.orange[100],
                        py: 1,
                        px: 2,
                        borderRadius: 2,
                        maxWidth: "400px",
                        border: `1px solid ${colors.orange[400]}`,
                    mx: 3,
                    }}
                >
                    <Text>{t("order_edit_1_sku")}</Text>
                </Box>
            )}

            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    p: 2,
                    mx: 3,
                    "@media screen and (max-width: 47.9375em)": {
                        p: 1
                    }
                }}
            >
                {newOrderLines.map((orderLine, index) => (
                    <Box key={"edit-order-line-" + index} flex={1}
                        sx={{
                            display: "flex",
                            gap: 2,
                            justifyContent: "flex-start",
                            "@media screen and (max-width: 47.9375em)": {
                                backgroundColor: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                boxShadow: theme.palette.mode === "dark" ? "none" : "0px 2px 4px rgba(0, 0, 0, 0.1)",
                                border: theme.palette.mode === "dark" ? "none" : "1px solid " + colors.grey[100],
                                borderRadius: 4,
                                py: 1.5,
                                px: 1.5,
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 2,
                                overflow: "hidden",
                            }
                        }}
                    >
                        <Box
                            sx={{
                                width: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 0.5,
                                "@media screen and (max-width: 47.9375em)": {
                                    overflow: "hidden",
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    width: 40,
                                    flexShrink: 0,
                                }}
                            >
                                <Text light>{t("sku")}</Text>
                            </Box>
                            <ShValidatedInputDropdown
                                height={40}
                                tabIndex={-1}
                                key={"edit-orderLine-product-" + orderLine.index}
                                displayName={"name"}
                                changeField={"id"}
                                valueKey={"id"}
                                closeOnSelection
                                searchOptions={{
                                    enabled: true,
                                    placeholder: t("search_product_placeholder"),
                                    searchables: ["barcodes",'id']
                                }}
                                styles={{
                                    flex: 1,
                                    minWidth: 200,
                                    "@media screen and (max-width: 47.9375em)": {
                                        minWidth: "unset",
                                    }
                                }}
                                maxWidth={400}
                                disallowDeselection
                                name={`${orderLine.index}.sku`}
                                selected={productOptions?.find(item => item.id && item.id === orderLine.sku)?.id ?? -1}
                                options={productOptions ? productOptions : []}
                                noSelection={t("choose_option")}
                                onChange={handleProductSelect}
                            />
                            {canDeleteLine(orderLine) ?
                                <Box
                                    tabIndex={-1}
                                    sx={{
                                        width: 40,
                                        display: "none",
                                        justifyContent: "flex-end",
                                        "@media screen and (max-width: 47.9375em)": {
                                            display: "flex",
                                        }
                                    }}
                                >
                                    <IconButton tabIndex={-1} type="button" color="red" onClick={() => handleDeleteLineRow(orderLine.index)} sx={{p: 1}}>
                                        <DeleteIcon/>
                                    </IconButton>
                                </Box>
                                : null
                            }
                        </Box>
                        <Box
                            sx={{
                                width: 200,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 0.5,
                                "@media screen and (max-width: 47.9375em)": {
                                    width: "unset",
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    width: 50,
                                    flexShrink: 0,
                                }}
                            >
                                <Text light>{t("quantity")}</Text>
                            </Box>
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                }}
                            >
                                <NumberInput
                                    btnSize={"24"}
                                    inputSX={{
                                        width: 144,
                                        "@media screen and (max-width: 47.9375em)": {
                                            px: 1,
                                            flex: 1,
                                            width: "unset",
                                        }
                                    }}
                                    value={orderLine.amount}
                                    change={(e) => handleChange(orderLine.index, 'amount', e)}
                                />

                                {canDeleteLine(orderLine) ?
                                    <Box
                                        tabIndex={-1}
                                        sx={{
                                            width: 40,
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            "@media screen and (max-width: 47.9375em)": {
                                                display: "none",
                                            }
                                        }}
                                    >
                                        <IconButton tabIndex={-1} type="button" color="red" onClick={() => handleDeleteLineRow(orderLine.index)} sx={{p: 1}}>
                                            <DeleteIcon/>
                                        </IconButton>
                                    </Box>
                                    : <Box tabIndex={-1} width={40} sx={{
                                        "@media screen and (max-width: 47.9375em)": {
                                            display: "none",
                                        }
                                    }}/>
                                }
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Box>
            <Box px={3} pt={2}>
                <EditViewActions onSave={handleSave} onCancel={handleCancel} isLoading={isSaving} />
            </Box>
        </Box>
    );
};

export const TimeLine = ({ logData, isRelation = false, allowedActions = null, horizontal: horizontalProp = false, webshopType = null }) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isSmallScreen = useMediaQuery('(max-width: 47.9375em)');

    // Horizontal layout: icons on top with connectors running across, scrolling sideways. The relation summary
    // opts in via the prop; small screens always use it. Otherwise the vertical timeline (icons left, lines down).
    const horizontal = horizontalProp || isSmallScreen;

    // Change logs, optionally filtered by action. Small screen keeps newest-first; desktop reverses to oldest-first.
    const logItems = (logData?.data || [])
        .filter(log => !allowedActions || allowedActions.includes(log.action?.toLowerCase()));
    const items = isSmallScreen ? logItems : [...logItems].reverse();

    return (
        <Box
            sx={{
                mr:3,
                mt:3,
                flex:1,
                p:2,
                minWidth:0,
                // Long unbroken log contents (e.g. tracking numbers) wrap instead of forcing a scrollbar.
                overflowWrap:"anywhere",
                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[50],
                borderRadius:4,
                ...(horizontal ? {
                    mr:0,
                    background:"none",
                    p:0,
                    mt:3,
                } : {})
            }}
        >
            <Subheading>{t("timeline")}</Subheading>
            <Box
                sx={{
                    ...(horizontal ? {
                        display:'flex',
                        minWidth:0,
                        overflowX:"auto",
                        overflowY:"hidden",
                        mt:1.5,
                    } : {})
                }}
            >
            {items.map((logline, idx) => {
                const settings = getTimeLineSettings(logline.action?.toLowerCase(), theme.palette.mode, isRelation);
                if (!settings) return null;
                const { comp: Component, check, icon: TimelineIcon } = settings;

                return (
                    <Box
                        key={"timeline"+idx}
                        sx={{
                            mt: horizontal ? 0 : 3,
                            display:"flex",
                            flexDirection: horizontal ? 'column' : 'row',
                            gap: horizontal ? 1 : 1.5,
                            position:"relative",
                            ...(horizontal ? { width:240, flexShrink:0, minWidth:0, pr:idx < (items.length -1) ? 5 : 0 } : {}),
                            "&:before" : {
                                content: '""',
                                display:"block",
                                position:"absolute",
                                background:colors[check][400],
                                ...(horizontal ? {
                                    top:11,
                                    left:24,
                                    height:2,
                                    width:idx < (items.length -1) ? 1 : 0,
                                } : {
                                    top:24,
                                    left:11,
                                    width:2,
                                    height:idx < (items.length -1) ? 1 : 0,
                                }),
                            }
                        }}
                    >
                        <Box
                            sx={{
                                flexShrink:0,
                                height:24,
                                width:24,
                                background:colors[check][400],
                                borderRadius:3,
                                color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                display:"flex",
                                alignItems:'center',
                                justifyContent:"center",
                                fontWeight:"bold"
                            }}
                        >
                            {TimelineIcon ? <TimelineIcon fontSize="small"/> : <CheckOutlinedIcon fontSize="small"/>}
                        </Box>
                        <Component key={logline.type+idx} {...logline} isRelation={isRelation} webshopType={webshopType} />
                    </Box>
                );
            })}
            </Box>

        </Box>
    )
}

const gridOptions = {
    export:true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
    hide:true,
    

};

const Summary = (props) => {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    
    const [isExtended, setIsExtended] = useState(false);
    const [isVirtualExtended, setIsVirtualExtended] = useState(false);
    const [spStockLoading, setSpStockLoading] = useState();
    const [spVirtualProductLoading, setSpVirtualProductLoading] = useState();

    const hasInvoicePermission = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.INVOICE_READ, UserPrivileges.INVOICE_MANAGE])
    );
    const hasInvoiceManagePermission = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.INVOICE_MANAGE])
    );

    const [returnIsExtended, setReturnIsExtended] = useState(false);
    const [returnSpLoading, setReturnSpLoading] = useState();

    const [returnId, setReturnId] = useState();
    const returnIdRef = useRef(returnId);
    
    const getReturnDetails = (returnid) => {
    
        const newReturnId = parseInt(returnid);
        // Only update state if the productId has actually changed
        if (returnIdRef.current !== newReturnId) {
            if(!returnSpLoading) {
                setReturnSpLoading(true);
            }
            setReturnId(newReturnId);
            returnIdRef.current = newReturnId; // Update the ref to the new value
        }
        setReturnIsExtended(true);
    };

    const [productId, setProductId] = useState();
    const productIdRef = useRef(productId);

    const [virtualProductId, setVirtualProductId] = useState();
    const virtualProductIdRef = useRef(virtualProductId);

    const [config,setConfig] = useState({
        is_editting:null,
    });

    // Check if order is editable based on webshopcredentials and state
    // Editable if: (no webshopcredentials OR (not bol/shopify AND integrator null)) AND state in open/backorder/replenish
    const isOrderEditable = props.data && (
        (
            !props.data.webshopcredentials ||
            (
                (
                    props.data.webshopcredentials.webshop_type !== "bol" ||
                    props.data.deliveryMethod?.indexOf("vvb") === -1
                ) &&
                (props.data.webshopcredentials.integratorId === null || props.data.webshopcredentials.integratorId === 0)
            )
        )
        && [general_states.OPEN, general_states.BACKORDERED, general_states.REPLENISH].includes(props.data.state)
    );

    const {data: logData} = useGetLogsQuery({
        args: {
            page: 1,
            size: 100,
            body: {
                "type": "Order",
                "externalId": props.data?.id
            }
        }
    },{
        skip: !props.data && !props.data?.id
    })

    const { data: orderLinesData, isLoading: isLoadingLines } = useGetOrderLinesQuery({
        args: {
            id: props.orderId,
            page: 1,
            size: 1000,
            body: {}
        }
    }, {
        skip: !props.orderId
    });

    const hasReadPricePermission = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.OUTBOUND_ORDER_VALUE])
    );

    const orderTotals = useMemo(() => {
        if(!hasReadPricePermission) {
            return {
                sellingPrice:0,
                sellingPriceExVat:0,
            }
        }
        const lines = orderLinesData?.data ?? [];
        return lines.reduce((acc, line) => {
            acc.sellingPrice += Number(line.sellingPrice) || 0;
            acc.sellingPriceExVat += Number(line.sellingPriceExVAT) || 0;
            return acc;
        }, { sellingPrice: 0, sellingPriceExVat: 0 });
    }, [orderLinesData]);
    
    const ShipperLogoMap = {
        postnl: PostNLLogo,
        dhl: DhlLogo,
        dpd: DpdLogo,
        bol: BolLogo,
        cirro:CirroLogo,
        hst: HstLogo
      };

      const renderShipperLogo = (shipperType) => {
        // Get the logo component based on shipper type
        const LogoComponent = ShipperLogoMap[shipperType];

        // Render the logo component if it exists, or null otherwise
        return LogoComponent ? <LogoComponent className={shipperType} /> : null;

    };

    const handleInvoiceProductEdit = () => {
        console.log("edit")
    }

    const [creditOrderCosts,{creditIsLoading}] = useCreditOrderCostsMutation();
    const handleCreditOrderCosts = async (params) => {
        try {
            if (creditIsLoading) return;
            const response = await creditOrderCosts(props.data?.id).unwrap();
            showToastMessagePostRequest(t, response);
        } catch (err) {
            showToastMessageRequestError(t("create_failed"), t, err);
        }
    };
    
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const confirmModal = {
        open: confirmModalOpen,
        setOpen: setConfirmModalOpen,
    };

    const handleUpdateInvoiceProductModalClose = () => {
        updateInvoiceProductsModal.setOpen(false);
    };
    
    const [updateInvoiceProductsModalOpen, setUpdateInvoiceProductsModalOpen] = useState(false);
    const updateInvoiceProductsModal = {
        open: updateInvoiceProductsModalOpen,
        setOpen: setUpdateInvoiceProductsModalOpen,
    };

    const handleUpdateShipperModalClose = () => {
        updateShippersModal.setOpen(false);
    };
    
    const [updateShipperModalOpen, setUpdateShipperModalOpen] = useState(false);
    const updateShippersModal = {
        open: updateShipperModalOpen,
        setOpen: setUpdateShipperModalOpen,
    };

    const handleConfirmClose = () => {
        confirmModal.setOpen(false);
    };

    const preGetProductDetails = (params) => {
        if(params.type === "virtual_product") {
            getVirtualProductDetails(params);
        } else {
            getProductDetails(params);
        }
    }

    const getProductDetails = (params) => {
        if (!params) return;
    
        const newProductId = parseInt(params.product.id);
        // Only update state if the productId has actually changed
        if (productIdRef.current !== newProductId) {
            if(!spStockLoading){
                setSpStockLoading(true);
            }
            setProductId(newProductId);
            productIdRef.current = newProductId; // Update the ref to the new value
        }
            setIsExtended(true);
    };

    const getVirtualProductDetails = (params) => {
        if (!params) return;
    
        const newProductId = parseInt(params.product.id);
        // Only update state if the productId has actually changed
        if (virtualProductIdRef.current !== newProductId) {
            if(!spVirtualProductLoading){
                setSpVirtualProductLoading(true);
            }
            setVirtualProductId(newProductId);
            virtualProductIdRef.current = newProductId; // Update the ref to the new value
        }

        setIsVirtualExtended(true);
    };

    useEffect(() => {
        // console.log(logData);
        props.replaceView({
            ...props.config,
            gridOptions:gridOptions,
            afterHeader: (
                <Box>
                    <TimeLine
                        logData={logData}
                    />
                </Box>
            )
        });

        return () => {
            // console.log("unmounting warehouse");
        }
    },[props.config,logData]);
    
    const toBoolean = value => {
        if (value === true || value === false) return value;      // already boolean
        if (value === "true") return true;
        if (value === "false") return false;
        return Boolean(value); // fallback for other cases
    };

    return (
        <>

            <Box
                sx={{
                    height:1,
                    display:"flex",
                    // alignItems:"flex-start",
                    "@media screen and (max-width: 47.9375em)" : {
                        flexDirection:"column"
                    }
                }}
            >

                <Box
                    sx={{
                        flex:2,
                        "@media screen and (max-width: 47.9375em)" : {
                            width:1,
                        }
                    }}
                >
                    {(props.data?.linkedCosts && hasInvoicePermission && props.data?.linkedCosts.length > 0) &&
                        <Box
                            sx={{
                                px:3,
                                pt:3,
                            }}
                        >
                            <SHQuickAction
                            maxWidth={560}
                                component={
                                    <Box
                                        sx={{
                                            display:"flex",
                                            flexDirection:'column',
                                            pt:2,
                                            px:2,
                                        }}
                                    >   
                                        <>
                                            <Box
                                                sx={{
                                                    position:'relative',
                                                    display:"flex",
                                                    alignItems:"center",
                                                    gap:3,
                                                }}
                                            >
                                                <Text
                                                    light
                                                    sx={{width: "120px"}}
                                                >
                                                    {t("invoice")}
                                                </Text>
                                                <Box
                                                
                                                    sx={{
                                                        width:"180px",
                                                        display:"flex",
                                                        alignItems:"center",
                                                        borderRadius:4,
                                                        "&:hover" : {
                                                            background:colors.grey[200],
                                                            cursor:"pointer",
                                                        },
                                                        "@media screen and (max-width: 768px)" : {
                                                            // px:0.5,
                                                        },
                                                    }}
                                                >
                                                    <Text light>{t("description")}</Text>
                                                </Box>
                                                <Text
                                                    light
                                                    sx={{width: "80px"}}
                                                >
                                                    {t("amount")}
                                                </Text>
                                                <Text
                                                    light
                                                    sx={{width: "80px"}}
                                                >
                                                    {t("price")}
                                                </Text>
                                                <Text
                                                    light
                                                    sx={{width: "80px"}}
                                                >
                                                    {t("total")}
                                                </Text>
                                            </Box>

                                            {props.data?.linkedCosts.map((linked,index) => (
                                                <Box key={index+"pcs"}
                                                    sx={{
                                                        position:'relative',
                                                        minHeight:40,
                                                        py:1,
                                                        display:"flex",
                                                        alignItems:"center",
                                                        gap:3,
                                                    }}
                                                >
                                                    <Text
                                                    sx={{width: "120px"}}
                                                    >
                                                        {t("invoice")} {linked.invoice}
                                                    </Text>
                                                    <Box

                                                        sx={{
                                                            width:"180px",
                                                            display:"flex",
                                                            alignItems:"center",
                                                            borderRadius:4,
                                                            "&:hover" : {
                                                                background:colors.grey[200],
                                                                cursor:"pointer",
                                                            },
                                                            "@media screen and (max-width: 768px)" : {
                                                                // px:0.5,
                                                            },
                                                        }}
                                                    >
                                                        <Text
                                                            sx={{
                                                                wordBreak: "break-word",
                                                                overflowWrap: "break-word",
                                                            }}
                                                        >
                                                            {linked.description}
                                                        </Text>
                                                    </Box>
                                                    <Text
                                                    sx={{width: "80px"}}
                                                    >
                                                        {parseFloat(linked.amount)}
                                                    </Text>
                                                    <Text
                                                    sx={{width: "80px"}}
                                                    >
                                                        {formatPrice(i18n, linked.price)}
                                                    </Text>
                                                    <Text
                                                    bold
                                                    sx={{width: "80px"}}
                                                    >
                                                        {formatPrice(i18n, parseFloat(linked.amount * linked.price))}
                                                    </Text>
                                                </Box>
                                            ))}
                                        </>
                                    </Box>
                                }
                            >
                                {({anchorEl,onMouseEnter,onMouseLeave,onTouchStart}) => (
                                    <Box
                                        aria-owns={anchorEl ? 'simple-popover' : undefined}
                                        aria-haspopup="true"
                                        onMouseEnter={onMouseEnter}
                                        onMouseLeave={onMouseLeave}
                                        onTouchStart={onTouchStart} 
                                        sx={{
                                            background: theme.palette.mode === "dark" ? colors.grey[100] : colors.grey[50],
                                            p:1.5,
                                            borderRadius:5,
                                            display:"flex",
                                            justifyContent:'space-between',
                                            alignItems:"center"
                                        }}
                                    >
                                        <Subheading light>
                                            {t("total_price")}
                                        </Subheading>
                                        <Box sx={{ display: "flex", alignItems: "center"}}>
                                        <Heading>{formatPrice(i18n, props.data?.totalCost)}</Heading>
                                        {hasInvoiceManagePermission && (
                                            <Box sx={{ marginLeft: 1}}>
                                            <IconButton type="button" onClick={() => updateInvoiceProductsModal.setOpen(true)}
                                            sx={{ 
                                                color: colors.txt["primary"]
                                            }}>
                                                <EditIcon/>
                                            </IconButton>
                                            {!props.data?.isCredited && (
                                            <IconButton type="button" onClick={() => confirmModal.setOpen(true)}
                                            sx={{ 
                                                color: colors.red[400]
                                            }}>
                                                <UndoIcon/>
                                            </IconButton>
                                            )}
                                            </Box>
                                        )}
                                        </Box>
                                    </Box>
                                )}
                            </SHQuickAction>
                        </Box>
                    }
                    { (props.data?.remark &&  props.data?.remark.length > 0) &&
                        <Box
                            sx={{
                            }}
                        >
                            <Subheading sx={{pl:3,pt:2,}}>{t("remark")}</Subheading>
                            <Box
                                sx={{
                                    mx:3,
                                    my:1.5,
                                    p:1.5,
                                    background: theme.palette.mode === "dark" ? colors.grey[100] : colors.grey[100],
                                    borderRadius:5,
                                }}
                            >
                                <Text>
                                    {props.data?.remark}
                                </Text>
                            </Box>
                        </Box>
                    }

                    <Box
                        sx={{
                            mt:3,
                            px:3,
                            display:"flex",
                            "@media screen and (max-width: 47.9375em)" : {
                                mr:0,
                            }
                        }}
                    >
                        <Box
                            sx={{
                                flex:props.data?.shippingAddress?.pickupPoint ? 1.5 :1,
                                display:"flex",
                                flexDirection:"column",
                            }}
                        >
                            <Subheading>{t("ordered_by")}</Subheading>
                            <Box
                                sx={{
                                    flex: 1,
                                    mt:1,
                                    p:2,
                                    borderRadius:4,
                                    display:"flex",
                                    flexDirection:"column",
                                    background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[50]
                                }}
                            >
                                
                                <Text bold variant={theme.palette.mode === "dark" ? "blue" : "primary"}>
                                    {props.data?.deliveryName}
                                </Text>
                                {props.data?.customerReference?.length > 0 && (
                                    <Box sx={{mb:props.data?.shippingAddress?.pickupPoint ? 0 : 1, mt:1, display:"flex", flexDirection:'column'}}>
                                        <Text bold>{t("customer_reference")}:</Text>
                                        <Text>{props.data.customerReference}</Text>
                                    </Box>
                                )}
                                {props.data?.shippingAddress?.pickupPoint ? 
                                    <Box
                                        sx={{
                                            mt:2,
                                            display:'flex',
                                            gap:1.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                flex:1,
                                                borderRadius:3,
                                                background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    borderTopLeftRadius:12,
                                                    borderTopRightRadius:12,
                                                    p:1,
                                                    py:0.5,
                                                    background:theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[200],
                                                }}
                                            >

                                                <Text bold>{t("pickuppoint_address")}</Text>
                                            </Box>
                                            <Box
                                                sx={{
                                                    p:1,
                                                    display:"flex",
                                                    flexDirection:'column',
                                                }}
                                            >
                                                <Text sx={{mt:0.5}} bold>{props.data?.shippingAddress?.pickupPointName}</Text>
                                                <Text >{props.data?.shippingAddress?.pickupPointData?.address}</Text>
                                                <Text light>{props.data?.shippingAddress?.pickupPointData?.postalCode}, {props.data?.shippingAddress?.pickupPointData?.city}</Text>
                                                <Text light>{props.data?.shippingAddress?.pickupPointData?.country}</Text>
                                            </Box>
                                        </Box>
                                        <Box
                                            sx={{
                                                p:1,
                                                flex:1,
                                                display:"flex",
                                                flexDirection:'column',
                                            }}
                                        >
                                            <Text bold>{t("original_address")}</Text>
                                            <Text sx={{mt:2}}>{props.data?.shippingAddress?.addressLine1}</Text>
                                            <Text light>{props.data?.shippingAddress?.postalCode}, {props.data?.shippingAddress?.city}</Text>
                                            <Text light>{(props.data?.shippingAddress?.state && props.data?.shippingAddress?.state !== null) && `${props.data?.shippingAddress?.state}, `}{props.data?.shippingAddress?.country}</Text>
                                        </Box>

                                    </Box> 
                                    :
                                    <Box
                                        sx={{
                                            mt:"auto",
                                            display:"flex",
                                            flexDirection:'column',
                                        }}
                                    >
                                        <Text bold>{t("address")}</Text>
                                        <Text>{props.data?.shippingAddress?.addressLine1}</Text>
                                        <Text light>{props.data?.shippingAddress?.postalCode}, {props.data?.shippingAddress?.city}</Text>
                                        <Text light>{(props.data?.shippingAddress?.state && props.data?.shippingAddress?.state !== null) && `${props.data?.shippingAddress?.state}, `}{props.data?.shippingAddress?.country}</Text>
                                    </Box>
                                }
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                flex:1,
                                ml:1,
                                display:'flex',
                                flexDirection:'column',
                            }}
                        >
                            <Subheading>{props.data?.state === "shipped" ? t("shipped_by") : t("shipping_by")}</Subheading>
                            <Box
                                sx={{
                                    flex:1,
                                    display:"flex",
                                    flexDirection:'column',
                                    gap:1,
                                }}
                            >
                                {(() => {
                                    const shipper = getShipperColorSettingsByType(props.data?.shipper?.type);
                                    return (
                                        <Box
                                            sx={{
                                                mt:1,
                                                flex:1,
                                                background: shipper.background,
                                                borderRadius:4,
                                                p:2,  
                                                display:'flex',
                                                gap:1.5,
                                                position:"relative",
                                                "& .postnl" : {
                                                    width:24,
                                                    height:24,
                                                },
                                
                                                "& .dpd" : {
                                                    width:24,
                                                    height:24,
                                                },
                                                "& .dhl" : {
                                                    width:24,
                                                    height:24,
                                                },
                                                "& .bol" : {
                                                    mt:0.5,
                                                    width:32,
                                                    // width:24,
                                                    height:16,
                                                },
                                                "& .hst" : {
                                                    width:72,
                                                    height:24,
                                                }
                                            }}
                                        >
                                            {renderShipperLogo(props.data?.shipper?.type)}
                                            <Box>
                                                <Text bold color={tokens("light").txt["primary"]}>{props.data?.shipper?.name}</Text><br/>
                                                <Text color={tokens("light").txt["primary"]}>{props.data?.shipperProfile?.name}</Text><br/>
                                                {props.data?.labelData && (
                                                    <Text light>{props.data.labelData?.labelInfo?.labelDisplayName ?? t("label_to_be_determined")}</Text>
                                                )}
                                            </Box>
                                            {(props.data?.state !== general_states.SHIPPED && props.data?.state !== general_states.CANCELLED) &&
                                                <ButtonBase
                                                    onClick={() => updateShippersModal.setOpen(true)}
                                                    sx={{
                                                        ml:"auto",
                                                        mb:"auto",
                                                        color:getShipperColorSettingsByType(props.data?.shipper?.type).base,
                                                        height:24,
                                                        width:24,
                                                        borderRadius:"50%",
                                                    }}
                                                >
                                                    <EditOutlinedIcon fontSize="small" />
                                                </ButtonBase>
                                            }
                                        </Box>
                                    );
                                })()}
                                
                                {selectedEnvironmentType === ENV_TYPES[100] &&
                                    <Box
                                        sx={{
                                            flex:1,
                                            background: colors.green[100],
                                            borderRadius:4,
                                            p:2,
                                            display:'flex',
                                            gap:1.5,
                                            position:'relative',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                height:32,
                                                width:32,
                                                borderRadius:4,
                                                background:colors.green[200],
                                            }}
                                        >

                                        </Box>
                                        <Box>

                                            <Text light>{t("carrier_advice")}</Text><br/>
                                            <Text>
                                                {props.data?.suggestedCarrier && props.data?.suggestedCarrier?.id > 0 ? 
                                                    props.data?.suggestedCarrier?.name : 
                                                    t("no_carrier_advice")
                                                }
                                            </Text>
                                        </Box>

                                        {props.data?.carriers?.length > 0 && (
                                            <SHQuickAction
                                                component={
                                                    <Box sx={{ ml: 0.5 }}>
                                                        <Subheading sx={{ mt: 0.5 }}>
                                                            {t("carriers")}
                                                        </Subheading>
                                                        <Box
                                                            sx={{
                                                                mt: 2,
                                                                maxHeight: 500,
                                                                display:"flex",
                                                                flexDirection:"column",
                                                                gap:1,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display:"flex",
                                                                    gap:2,
                                                                }}
                                                            >
                                                                <Text light sx={{width:150}}>{t("name")}</Text>
                                                                <Text light sx={{width:120}}>{t("length")}</Text>
                                                                <Text light sx={{width:120}}>{t("width")}</Text>
                                                                <Text light sx={{width:120}}>{t("height")}</Text>
                                                            </Box>
                                                            {props.data?.carriers?.map((option, idx) => (
                                                                <Box 
                                                                    key={idx}
                                                                    sx={{
                                                                        display:"flex",
                                                                        gap:2,
                                                                    }}
                                                                >
                                                                    <Text sx={{width:150}}>{option.carrier.name}</Text>
                                                                    <Text sx={{width:120}}>{option.carrier.length} {t("mm")}</Text>
                                                                    <Text sx={{width:120}}>{option.carrier.width} {t("mm")}</Text>
                                                                    <Text sx={{width:120}}>{option.carrier.height} {t("mm")}</Text>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                }
                                            >
                                                {({anchorEl, onMouseEnter, onMouseLeave, onTouchStart }) => (
                                                    <Box
                                                        aria-owns={anchorEl ? "simple-popover" : undefined}
                                                        aria-haspopup="true"
                                                        onMouseEnter={onMouseEnter}
                                                        onMouseLeave={onMouseLeave}
                                                        onTouchStart={onTouchStart}
                                                        sx={{
                                                            background:colors.green[100],
                                                            borderLeft:`1px solid ${colors.green[400]}`,
                                                            borderBottom:`1px solid ${colors.green[400]}`,
                                                            fontSize:16,
                                                            height:32,
                                                            px:2,
                                                            position:"absolute",
                                                            right:0,
                                                            top:0,
                                                            borderTopRightRadius:16,
                                                            borderBottomLeftRadius:16,
                                                            display:"flex",
                                                            alignItems:"center",
                                                            justifyContent:'center',
                                                            "& svg" : {
                                                                width:24,
                                                                height:24,
                                                                "& path" : {
                                                                    fill:colors.green[400],

                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <Crate/>
                                                    </Box>
                                                )}
                                            </SHQuickAction>
                                        )}
                                    </Box>
                                }
                            </Box>
                        </Box>
                      
                    </Box>
                    
                    {/* Orderlines */}
                    {config.is_editting !== null && isOrderEditable ? (
                        <OrderLinesEditView
                            data={props.data}
                            config={config}
                            setConfig={setConfig}
                        />
                    ) : (
                        <Box sx={{ p: 3 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    "@media screen and (max-width: 47.9375em)": {
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        gap: 2
                                    }
                                }}
                            >
                                <Subheading
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)": {
                                            display: "none",
                                        }
                                    }}
                                >
                                    {t("orderlines")}
                                </Subheading>
                                {isOrderEditable && (
                                    <Shbutton
                                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                        variant="contained"
                                        size="small"
                                        startIcon={<EditIcon />}
                                        onClick={() => setConfig(config => ({ ...config, is_editting: false }))}
                                    >
                                        {t("edit")}
                                    </Shbutton>
                                )}
                            </Box>
                            {hasReadPricePermission &&
                                <Box
                                    sx={{
                                        mt:2,
                                        p:2,
                                        py:1.5,
                                        borderRadius:4,
                                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                    }}
                                >
                                    <Text bold>
                                        {t("total_order_price")}
                                    </Text>
                                    <Box 
                                        sx={{ 
                                            mt:1.5,
                                            display:"flex", 
                                            justifyContent:"space-between", 
                                            alignItems:"center" 
                                        }}
                                    >
                                        <Text light>
                                            {t("total_price_ex_vat")}
                                        </Text>
                                        
                                        <Text bold>
                                            {formatPrice(t, orderTotals.sellingPriceExVat)}
                                        </Text>
                                    </Box>
                                    {(orderTotals.sellingPrice -orderTotals.sellingPriceExVat) > 0 &&
                                        <Box 
                                            sx={{ 
                                                display:"flex", 
                                                justifyContent:"space-between", 
                                                alignItems:"center" 
                                            }}
                                        >
                                            <Text light>
                                                {t("total_vat")}
                                            </Text>
                                            
                                            <Text>
                                                {formatPrice(t, (orderTotals.sellingPrice -orderTotals.sellingPriceExVat))}
                                            </Text>
                                        </Box>
                                    }
                                    <Box 
                                        sx={{ 
                                            mt:0.5,
                                            pt:0.5,
                                            borderTop:`1px solid ${ theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[200]}`,
                                            display:"flex", 
                                            justifyContent:"space-between", 
                                            alignItems:"center" 
                                        }}
                                    >
                                        <Text>
                                            {t("total_price")}
                                        </Text>
                                        
                                        <Text bold>
                                            {formatPrice(t, (orderTotals.sellingPrice))}
                                        </Text>
                                    </Box>
                                </Box>
                            }
                            <Box sx={{ mx: -3,mt: hasReadPricePermission ? -1 : null, }}>
                                <OrderlinesComp
                                    getProductDetails={getProductDetails}
                                    getVirtualProductDetails={getVirtualProductDetails}
                                    orderId={props.orderId}
                                    orderLinesData={orderLinesData}
                                    isLoadingLines={isLoadingLines}
                                    hideTitle={true}
                                />
                            </Box>
                        </Box>
                    )}
                    <OrderEvents orderId={props.data?.id} sx={{ pb: props.data?.returns?.length > 0 ? 3 : 0 }} />
                    {props.data?.returns?.length > 0 && (
                        <Box
                            sx={{
                                px:3,
                            }}
                        >
                            <Subheading
                                sx={{
                                    pb:1.5,
                                }}
                            >
                                {t("returns")}
                            </Subheading>
                            <Box
                                sx={{
                                    display:"flex",
                                    flexDirection:'column',
                                    gap:1.5,
                                }}
                            >
                                { props.data.returns.map((item,index) => (
                                    <ButtonBase 
                                        onClick={() => {
                                            getReturnDetails(item.id);
                                        }}
                                        sx={{
                                            borderRadius:3,
                                            px:2,
                                            py:1.5,
                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                            border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                            boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                            display:"flex",
                                            flexDirection:'column',
                                            alignItems:'flex-start',
                                            justifyContent:'flex-start',
                                        }}
                                    >
                                        <Text bold>
                                            {item.reference}
                                        </Text>
                                        <Text light>
                                            {item.amountOfLines} {t("lines")}
                                        </Text>
                                    </ButtonBase>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        flex:1,
                        "@media screen and (max-width: 47.9375em)" : {
                            display:'none',
                        }
                    }}
                >
                    {props.data?.orderFieldValues?.some(
                        ofv => ofv.value !== null && ofv.value !== "" && ofv.value !== undefined
                    ) && (
                        <Box
                            sx={{
                                pr:3,
                                pt:3,
                            }}
                        >
                            <Subheading>{t("custom_fields_orders")}</Subheading>
                            <Box
                                sx={{
                                    display:"flex",
                                    flexDirection:"column",
                                    gap:1,
                                    mt:1,

                                }}
                            >
                                {props.data?.orderFieldValues
                                    .filter(ofv =>
                                        ofv.value !== null &&
                                        ofv.value !== "" &&
                                        ofv.value !== undefined
                                    )
                                    .map((ofv, ofvidex) => {
                                        
                                        return (
                                            booleanValueTypes.includes(ofv.orderfield.valueType) ?
                                            <Box
                                                key={"ofv" + ofvidex}
                                                sx={{
                                                    height:24,
                                                    border:`1px solid ${toBoolean(ofv.value) ? colors.green[400] : colors.red[400]}`,
                                                    background: toBoolean(ofv.value) ? colors.green[100] : colors.red[100],
                                                    borderRadius:3,
                                                    display:'flex',
                                                    alignItems:"center",
                                                    justifyContent:"space-between",
                                                    px:1,
                                                    "@media screen and (max-width: 47.9375em)" : {
                                                        flexWrap:"wrap",
                                                        height:"unset",
                                                        py:0.5,
                                                    }
                                                }}
                                            >
                                                <Text>
                                                    {ofv.orderfield?.name}
                                                </Text>

                                                <Box
                                                    sx={{
                                                        color: toBoolean(ofv.value) ? colors.green[400] : colors.red[400],
                                                        fontSize:14,
                                                        fontWeight:700,
                                                        fontFamily:"Roboto,sans-serif",
                                                        lineHeight:"16px",
                                                    }}
                                                >
                                                    {t(toBoolean(ofv.value) ? "yes" : 'no')}
                                                </Box>
                                            </Box>
                                            :
                                            <Box
                                                key={"ofv"  + ofvidex}
                                                sx={{
                                                    height:24,
                                                    border:`1px solid ${theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[200]}`,
                                                    background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                                                    borderRadius:3,
                                                    display:'flex',
                                                    alignItems:"center",
                                                    justifyContent:"space-between",
                                                    px:1,
                                                    "@media screen and (max-width: 47.9375em)" : {
                                                        flexWrap:"wrap",
                                                        height:"unset",
                                                        py:0.5,
                                                    }
                                                }}
                                            >
                                                <Text light>
                                                    {ofv.orderfield?.name}
                                                </Text>

                                                <Text>
                                                    {ofv.value.toString()}
                                                </Text>
                                            </Box>

                                        )
                                    })
                                }
                            </Box>
                        </Box>
                    )}
                    <TimeLine 
                        logData={logData}
                    />
                </Box>
               
            </Box>
            <MConfirmation
                open={confirmModal.open}
                handleClose={handleConfirmClose}
                title={t("mc_credit_order_costs")}
                subtitle={t("mc_credit_order_costs_subtitle")}
                confirm={() => handleCreditOrderCosts()}
            />

            <MUpdateOrderInvoiceProducts
                open={updateInvoiceProductsModal.open}
                handleClose={handleUpdateInvoiceProductModalClose}
                orderData={props.data}
            ></MUpdateOrderInvoiceProducts>

            <MUpdateOrderShipper 
                open={updateShippersModal.open}
                handleClose={handleUpdateShipperModalClose}
                warehouseId={props.data?.warehouseId}
                shipper={props.data?.shipper}
                shipperProfile={props.data?.shipperProfile}
                webshopcredentials={props.data?.webshopcredentials}
                orderId={props.data?.id}
            />

            {spStockLoading && 
                <SpStockDetails
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    productId={productId}
                />
            }

            {spVirtualProductLoading && 
                <SpVirtualProductDetails
                    isExtended={isVirtualExtended}
                    setIsExtended={setIsVirtualExtended}
                    virtualProductId={virtualProductId}
                    actionButtons={true}
                />
            }

            {returnSpLoading && 
                <SpReturnDetails
                    returnId={returnId}
                    isExtended={returnIsExtended}
                    setIsExtended={setReturnIsExtended}
                    // data={pickingJobData}
                /> 
            }
            
        </>
    );
}

export default Summary;